function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useMemo, useState } from 'react';
import { Button, Card, TextButton, Typography, Popover, CloseButton } from 'seagull';
import colors from 'seagull/foundations/colors.module.scss';
import { LogoPicker } from "../../../components/LogoPicker/LogoPicker";
import { ColorPicker } from "../../../components/ColorPicker/ColorPicker";
import { ColorSelector } from "../../../components/ColorSelector/ColorSelector";
import { useCobBranding } from "../CobBrandingContext";
import styles from "./BrandingControllers.module.scss";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { useCobConfig } from "../CobConfigContext";
import { useBrandingActions } from "../BrandingActionsContext";
import { PreviewState } from "../ClientOnboarding.utils";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const SectionTitle = _ref => {
  let {
    children
  } = _ref;
  return /*#__PURE__*/_jsx(Typography, {
    weight: "normal",
    children: children
  });
};
const ColorControllers = _ref2 => {
  let {
    children
  } = _ref2;
  return /*#__PURE__*/_jsx("div", {
    className: styles.colorControllers,
    children: children
  });
};
const ColorsLabel = _ref3 => {
  let {
    children
  } = _ref3;
  return /*#__PURE__*/_jsx(Typography, {
    skin: "secondary",
    children: children
  });
};
const Separator = () => /*#__PURE__*/_jsx("div", {
  className: styles.sep
});
function ColorSelectorWithPopup(props) {
  const {
    branding,
    colorKey,
    hasError,
    onChange
  } = props;
  const color = branding[colorKey];
  const [anchorEl, setAnchorEl] = useState(null);
  const presetColors = useMemo(() => [colors.orange900, colors.orange800, colors.orange700, colors.orange500, colors.orange300, colors.orange50, colors.green900, colors.green800, colors.green700, colors.green500, colors.green300, colors.green50, colors.blue800, colors.blue700, colors.blue600, colors.blue500, colors.blue300, colors.blue50, colors.purple900, colors.purple800, colors.purple700, colors.purple500, colors.purple300, colors.purple50, colors.dark900, colors.dark800, colors.dark700, colors.dark500, colors.dark300, colors.dark100, colors.red970, colors.red950, colors.red900, colors.red700, colors.red500, colors.red300], []);
  function changeColor(newColor) {
    onChange(_objectSpread(_objectSpread({}, branding), {}, {
      [colorKey]: newColor
    }));
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ColorSelector, {
      withBorder: true,
      color: color,
      hasError: hasError,
      testid: "color-selector-".concat(colorKey),
      onClick: e => setAnchorEl(e.currentTarget)
    }), /*#__PURE__*/_jsx(Popover, {
      anchorEl: anchorEl,
      open: !!anchorEl,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'center'
      },
      transformOrigin: {
        vertical: -5,
        horizontal: 'center'
      },
      onClose: () => setAnchorEl(null),
      children: /*#__PURE__*/_jsxs(Card, {
        padding: "s",
        children: [/*#__PURE__*/_jsx(ColorPicker, {
          className: styles.colorPicker,
          testid: "color-picker-".concat(colorKey),
          color: color,
          displayColor: color,
          onChange: newColor => changeColor(newColor)
        }), /*#__PURE__*/_jsx(Separator, {}), /*#__PURE__*/_jsx("div", {
          className: styles.colorsGrid,
          children: presetColors.map((color, idx) => /*#__PURE__*/_jsx(ColorSelector, {
            withBorder: true,
            color: color,
            testid: "color-preset-".concat(idx),
            onClick: () => changeColor(color)
          }, color))
        })]
      })
    })]
  });
}
export default function BrandingControllers(_ref4) {
  let {
    worksAt: _
  } = _ref4;
  const {
    branding,
    logoImage,
    errors,
    isDirty,
    isValid,
    setBranding,
    setLogo,
    revertToDefault,
    saveLogoAndBranding
  } = useCobBranding();
  const {
    SET_UP_BRAND_CLIENT_PREVIEW = false
  } = useFeatureFlags();
  const {
    originalPreviewState
  } = useCobConfig();
  const {
    onToggleBrandSetup
  } = useBrandingActions();
  function handleCloseClick() {
    onToggleBrandSetup();
  }
  function getColorControl(colorKey) {
    return /*#__PURE__*/_jsx(ColorSelectorWithPopup, {
      branding: branding,
      hasError: errors.has(colorKey),
      colorKey: colorKey,
      onChange: setBranding
    });
  }
  return /*#__PURE__*/_jsxs(Card, {
    padding: "s",
    className: styles.brandingCard,
    children: [SET_UP_BRAND_CLIENT_PREVIEW ? /*#__PURE__*/_jsxs("div", {
      className: styles.headerContainer,
      children: [/*#__PURE__*/_jsx(SectionTitle, {
        children: 'Customize brand'
      }), originalPreviewState !== PreviewState.SetupBrand && /*#__PURE__*/_jsx(CloseButton, {
        skin: "standard",
        className: styles.closeBtn,
        onClick: handleCloseClick,
        "data-testid": "close-branding-setup-btn"
      })]
    }) : /*#__PURE__*/_jsx(SectionTitle, {
      children: 'Upload logo'
    }), /*#__PURE__*/_jsxs("div", {
      className: styles.scrollableContent,
      children: [/*#__PURE__*/_jsx(LogoPicker, {
        isNewUI: true,
        testid: "upload-logo",
        image: logoImage,
        onChange: setLogo
      }), /*#__PURE__*/_jsx(SectionTitle, {
        children: "Choose brand colors"
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.colorsSection,
        children: [/*#__PURE__*/_jsx(ColorsLabel, {
          children: "Text"
        }), /*#__PURE__*/_jsxs(ColorControllers, {
          children: [getColorControl('textStandard'), getColorControl('textSecondary')]
        }), /*#__PURE__*/_jsx(ColorsLabel, {
          children: "Buttons"
        }), /*#__PURE__*/_jsxs(ColorControllers, {
          children: [getColorControl('buttonPrimary'), getColorControl('buttonSecondary'), getColorControl('buttonDisabled')]
        }), /*#__PURE__*/_jsx(ColorsLabel, {
          children: "Background"
        }), /*#__PURE__*/_jsxs(ColorControllers, {
          children: [getColorControl('backgroundGradientStart'), getColorControl('backgroundGradientEnd'), getColorControl('backgroundLight'), getColorControl('backgroundStandard')]
        }), /*#__PURE__*/_jsx(ColorsLabel, {
          children: "Progress bar"
        }), /*#__PURE__*/_jsxs(ColorControllers, {
          children: [getColorControl('progressBarComplete'), getColorControl('progressBarIncomplete')]
        })]
      }), /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(TextButton, {
          "data-testid": "undo-color-changes",
          underline: "none",
          onClick: revertToDefault,
          children: "Revert to default colors"
        })
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.saveSection,
        children: [/*#__PURE__*/_jsx(Button, {
          skin: "primaryOutline",
          disabled: !isDirty || !isValid,
          "data-testid": "save-colors",
          tooltip: {
            placement: 'top',
            title: !isDirty ? 'Nothing to save' : !isValid ? 'One of the fields is invalid' : ''
          },
          onClick: saveLogoAndBranding,
          className: SET_UP_BRAND_CLIENT_PREVIEW ? styles.saveBtn : styles.saveBtnOld,
          children: "Save changes"
        }), SET_UP_BRAND_CLIENT_PREVIEW && /*#__PURE__*/_jsx(Typography, {
          variant: "paragraph2",
          skin: "secondary",
          className: styles.saveChangesInfo,
          children: "Changes saved here will apply to all of your proposals."
        })]
      })]
    })]
  });
}